<template>
    <div class="bg-base-200 h-screen-header rounded-xl md:p-4 overflow-y-auto fade-in">
        <div class="p-4 md:p-6">
            <div class="flex items-center justify-between gap-4 mb-6">
                <div>
                    <div class="text-sm font-bold text-base-content/50 uppercase tracking-wider">{{ $tt('Search') }}</div>
                    <h1 class="text-3xl font-bold text-base-content">{{ $tt('音乐搜索') }}</h1>
                </div>
            </div>
        
            <div class="bg-base-100 rounded-2xl p-4 md:p-5 shadow-lg mb-6">
                <div class="relative">
                    <input
                        ref="desktopInput"
                        v-model.trim="searchTerm"
                        type="search"
                        class="input input-bordered w-full pr-12"
                        :placeholder="$tt('搜索艺术家、专辑或歌曲...')"
                        @input="handleInput"
                    >
                    <button
                        v-if="searchTerm"
                        class="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/45 hover:text-base-content"
                        @click="clearSearch"
                    >
                        <svg viewBox="0 0 24 24" class="w-5 h-5" fill="currentColor">
                            <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.7 2.88 18.3 9.17 12 2.88 5.71 4.29 4.3l6.3 6.29 6.29-6.3z"></path>
                        </svg>
                    </button>
                </div>
            </div>
        
            <div v-if="isLoading" class="text-center text-base-content/60 py-16">
                {{ $tt('正在搜索...') }}
            </div>
        
            <div v-else-if="!hasKeyword" class="text-center text-base-content/60 py-16">
                {{ $tt('输入关键词后开始搜索') }}
            </div>
        
            <div v-else-if="showEmpty" class="text-center text-base-content/60 py-16">
                {{ $tt('未找到相关结果') }}
            </div>
        
            <div v-else class="space-y-6">
                <section v-if="artists.length" class="bg-base-100 rounded-2xl p-5 shadow-lg">
                    <div class="flex items-center justify-between mb-4">
                        <h2 class="text-xl font-bold">{{ $tt('艺术家') }}</h2>
                        <span class="text-sm text-base-content/50">{{ artists.length }}</span>
                    </div>
                    <div class="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                        <button
                            v-for="artist in artists"
                            :key="artist.id"
                            class="flex items-center gap-3 p-3 rounded-xl hover:bg-base-200 text-left"
                            @click="redirectArtist(artist)"
                        >
                            <img :src="artist.image || '/static/dist/img/album_null.jpg'" :alt="artist.name" class="w-14 h-14 rounded-full object-cover">
                            <div class="min-w-0">
                                <div class="font-semibold truncate">{{ artist.name }}</div>
                                <div class="text-sm text-base-content/60">{{ $tt('{count} 张专辑', { count: artist.albumCount || 0 }) }}</div>
                            </div>
                        </button>
                    </div>
                </section>
        
                <section v-if="albums.length" class="bg-base-100 rounded-2xl p-5 shadow-lg">
                    <div class="flex items-center justify-between mb-4">
                        <h2 class="text-xl font-bold">{{ $tt('专辑') }}</h2>
                        <span class="text-sm text-base-content/50">{{ albums.length }}</span>
                    </div>
                    <div class="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-4">
                        <button
                            v-for="album in albums"
                            :key="album.id"
                            class="text-left"
                            @click="redirectAlbum(album)"
                        >
                            <img :src="album.image || '/static/dist/img/album_null.jpg'" :alt="album.name" class="w-full aspect-square rounded-xl object-cover mb-2">
                            <div class="font-semibold truncate">{{ album.name }}</div>
                            <div class="text-sm text-base-content/60 truncate">{{ album.artist }}</div>
                        </button>
                    </div>
                </section>
        
                <section v-if="tracks.length" class="bg-base-100 rounded-2xl p-5 shadow-lg">
                    <div class="flex items-center justify-between mb-4">
                        <h2 class="text-xl font-bold">{{ $tt('歌曲') }}</h2>
                        <span class="text-sm text-base-content/50">{{ tracks.length }}</span>
                    </div>
                    <div class="space-y-2">
                        <button
                            v-for="(track, index) in tracks"
                            :key="track.id || `desktop-track-${index}`"
                            class="w-full flex items-center gap-4 p-3 rounded-xl hover:bg-base-200 text-left"
                            @click="playTrack(index)"
                        >
                            <img :src="track.image || '/static/dist/img/album_null.jpg'" :alt="track.title" class="w-12 h-12 rounded-lg object-cover">
                            <div class="flex-1 min-w-0">
                                <div class="font-semibold truncate">{{ track.title }}</div>
                                <div class="text-sm text-base-content/60 truncate">{{ track.artist }}<span v-if="track.album"> · {{ track.album }}</span></div>
                            </div>
                            <div class="text-sm text-base-content/50">{{ formatDuration(track.duration) }}</div>
                        </button>
                    </div>
                </section>
            </div>
        </div>
    </div>
</template>
<script>
export default {
    name: 'LibrarySearch',
    data() {
        return {
            searchTerm: '',
            artists: [],
            albums: [],
            tracks: [],
            isLoading: false,
            searchTimeout: null
        }
    },
    computed: {
        hasKeyword() {
            return this.searchTerm.length > 0
        },
        showEmpty() {
            return this.hasKeyword && !this.isLoading && !this.artists.length && !this.albums.length && !this.tracks.length
        }
    },
    watch: {
        '$route.query.q'(value) {
            const keyword = typeof value === 'string' ? value.trim() : ''
            if (keyword === this.searchTerm) {
                return
            }
            this.searchTerm = keyword
            if (keyword) {
                this.fetchSearch(keyword)
            } else {
                this.resetResults()
            }
        }
    },
    mounted() {
        const keyword = typeof this.$route.query.q === 'string' ? this.$route.query.q.trim() : ''
        if (keyword) {
            this.searchTerm = keyword
            this.fetchSearch(keyword)
        }
        this.$nextTick(() => {
            const target = this.$refs.desktopInput
            if (target) {
                target.focus()
            }
        })
    },
    beforeDestroy() {
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = null
        }
    },
    methods: {
        handleInput() {
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout)
            }
            if (!this.searchTerm) {
                this.resetResults()
                this.syncQuery('')
                return
            }
            this.searchTimeout = setTimeout(() => {
                this.searchTimeout = null
                this.fetchSearch(this.searchTerm)
            }, 260)
        },
        submitSearch() {
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout)
                this.searchTimeout = null
            }
            this.fetchSearch(this.searchTerm)
        },
        async fetchSearch(keyword) {
            const query = keyword.trim()
            this.syncQuery(query)
            if (!query) {
                this.resetResults()
                return
            }
            this.isLoading = true
            try {
                const result = await this.$api.subsonic.search(query, 6, 0, 8, 0, 12, 0)
                this.artists = result.artists || []
                this.albums = result.albums || []
                this.tracks = result.tracks || []
            } catch (error) {
                console.error('搜索失败:', error)
                this.resetResults()
            } finally {
                this.isLoading = false
            }
        },
        syncQuery(query) {
            const current = typeof this.$route.query.q === 'string' ? this.$route.query.q : ''
            if (current === query) {
                return
            }
            this.$router.replace({
                path: this.$route.path,
                query: query ? { q: query } : {}
            })
        },
        resetResults() {
            this.isLoading = false
            this.artists = []
            this.albums = []
            this.tracks = []
        },
        clearSearch() {
            this.searchTerm = ''
            this.resetResults()
            this.syncQuery('')
            this.$nextTick(() => {
                const target = this.$refs.desktopInput
                if (target) {
                    target.focus()
                }
            })
        },
        redirectArtist(item) {
            this.$router.push({ path: '/artist/id/' + item.id })
        },
        redirectAlbum(item) {
            this.$router.push({ path: '/album/id/' + item.id })
        },
        playTrack(index) {
            this.$store.dispatch('playTrackList', {
                index,
                tracks: this.tracks
            })
        },
        formatDuration(value) {
            if (!isFinite(value)) {
                return '∞'
            }
            const minutes = Math.floor(value / 60)
            const seconds = Math.floor(value % 60)
            return (minutes < 10 ? '0' : '') + minutes + ':' + (seconds < 10 ? '0' : '') + seconds
        }
    }
}
</script>
